import Settings from "./settings";

/**
 * Front end half of the server side address validation.
 *
 * Two jobs, both about keeping the billable Kleber verification calls down and the user out of a
 * dead end:
 *
 * 1. Record that an address was filled from the DataTools autocomplete, so the server can accept
 *    it without a second API call. Any edit afterwards clears the record.
 * 2. Render the "my address is correct" checkbox after a soft validation failure, so confirming a
 *    genuinely new address is an explicit action rather than "submit the same thing again".
 */
class AddressValidation {

	/**
	 * Reduce one address part to its comparable form.
	 *
	 * Must stay in step with Address_Validation::normalise_parts() in PHP, or the server will fall
	 * back to an API call. That is a cost, not a bug, but it defeats the point.
	 *
	 * @param {string} value
	 * @returns {string}
	 */
	normalisePart(value) {
		return String(value === null || typeof value === "undefined" ? "" : value)
			.toUpperCase()
			.replace(/[^A-Z0-9 ]/g, "")
			.replace(/\s+/g, " ")
			.trim();
	}

	/**
	 * Reduce a set of address inputs to the string the server compares against.
	 *
	 * @param {Array} elements Address line 1, address line 2, city, state and postcode elements.
	 * @returns {string}
	 */
	normaliseElements(elements) {
		return elements
			.map((element) => this.normalisePart(element ? element.value : ""))
			.join("|");
	}

	/**
	 * Get the hidden input holding the validated state for an address group, creating it if needed.
	 *
	 * @param {HTMLElement} form
	 * @param {string} key
	 * @returns {HTMLInputElement|null}
	 */
	getStateInput(form, key) {
		if (!form) {
			return null;
		}

		const name = `datatools_address_validated[${key}]`;
		let input = form.querySelector(`input[name="${name}"]`);

		if (!input) {
			input = document.createElement("input");
			input.type = "hidden";
			input.name = name;
			form.appendChild(input);
		}

		return input;
	}

	/**
	 * Record that this address group was filled from a DataTools autocomplete selection.
	 *
	 * @param {HTMLElement} form
	 * @param {string} key
	 * @param {Array} elements
	 */
	recordSelection(form, key, elements) {
		const input = this.getStateInput(form, key);

		if (!input) {
			return;
		}

		input.value = this.normaliseElements(elements);
	}

	/**
	 * Clear the recorded selection for an address group.
	 *
	 * @param {HTMLElement} form
	 * @param {string} key
	 */
	clearSelection(form, key) {
		const input = this.getStateInput(form, key);

		if (input) {
			input.value = "";
		}
	}

	/**
	 * Clear the recorded selection if the address no longer matches what was recorded.
	 *
	 * Compared rather than cleared outright because not every one of these events means an edit.
	 * The user types into address line 1 before picking a suggestion, so that input is dirty, and
	 * the browser fires a native "change" on it when it loses focus. Reaching the submit button
	 * means losing focus, so clearing unconditionally threw the selection away on every submission
	 * and paid for a verification call on an address the autocomplete had already confirmed.
	 *
	 * The elements are resolved through a getter rather than captured, because only the elements
	 * not already watched get listeners on a re-watch. WooCommerce's country-select.js replaces the
	 * state node outright on a country change, so a captured array would leave the other four inputs
	 * comparing against a detached state node holding its pre-replacement value, and wiping the
	 * selection on the next edit.
	 *
	 * @param {HTMLElement} form
	 * @param {string} key
	 * @param {Function} getElements Returns address line 1, address line 2, city, state and postcode.
	 */
	watch(form, key, getElements) {
		getElements().forEach((element) => {
			if (!element || element.dataset.datatoolsValidationWatched) {
				return;
			}
			element.dataset.datatoolsValidationWatched = "1";

			// "change" covers the state <select>, which never fires "input" on a mouse selection.
			["input", "keyup", "paste", "change"].forEach((eventName) => {
				element.addEventListener(eventName, () => this.clearSelectionIfEdited(form, key, getElements()));
			});
		});
	}

	/**
	 * Drop the recorded selection when the address has actually been changed since it was recorded.
	 *
	 * @param {HTMLElement} form
	 * @param {string} key
	 * @param {Array} elements
	 */
	clearSelectionIfEdited(form, key, elements) {
		const input = this.getStateInput(form, key);

		if (!input || !input.value) {
			return;
		}

		if (input.value !== this.normaliseElements(elements)) {
			input.value = "";
		}
	}

	/**
	 * Find the form a checkbox rendered outside every form still has to be submitted with.
	 *
	 * Only the WooCommerce checkout renders the validation message outside its form, and only on
	 * the non AJAX path, where wc_print_notices() runs on woocommerce_before_checkout_form. The
	 * AJAX path prepends its notice group into form.checkout itself, so that copy is already
	 * covered by an anchor inside a form and never reaches here.
	 *
	 * @returns {HTMLElement|null}
	 */
	getFallbackForm() {
		return document.querySelector("form.checkout");
	}

	/**
	 * Pick the one anchor per address group that the confirmation checkbox belongs on, and the form
	 * its inputs have to be submitted with.
	 *
	 * The anchor travels inside the validation message, and a platform is free to render that
	 * message more than once: Gravity Forms repeats it in the error summary above the form, inside
	 * the summary's "jump to field" link and outside the form element. A checkbox there is both
	 * invalid markup and dead, because nothing outside the form is ever submitted.
	 *
	 * So an anchor inside a link is never used, and where a group has several candidates the one
	 * inside a form wins.
	 *
	 * @returns {Array} One {anchor, form} entry per address group key.
	 */
	getConfirmationAnchors() {
		const anchors = [...document.querySelectorAll(".datatools-address-confirm-anchor")].filter(
			(anchor) => anchor.id && !anchor.closest("a")
		);

		// One per form and group. Two address enabled forms on a page produce the same anchor id
		// twice and both need their own checkbox, so the form is part of the identity.
		const withinForms = new Map();
		const withoutForm = new Map();

		anchors.forEach((anchor) => {
			const form = anchor.closest("form");

			if (!form) {
				// Keyed the same way as the in form anchors: two form-less anchors for the same
				// group would otherwise be two checkboxes sharing one name.
				if (!withoutForm.has(anchor.id)) {
					withoutForm.set(anchor.id, anchor);
				}

				return;
			}

			if (!withinForms.has(form)) {
				withinForms.set(form, new Map());
			}

			const groups = withinForms.get(form);

			if (!groups.has(anchor.id)) {
				groups.set(anchor.id, anchor);
			}
		});

		const chosen = [...withinForms.entries()].flatMap(([form, groups]) =>
			[...groups.values()].map((anchor) => ({ anchor, form }))
		);
		const claimed = new Set(chosen.map((entry) => entry.anchor.id));
		const fallbackForm = this.getFallbackForm();

		// An anchor outside every form is used only where nothing inside a form covers the same
		// group, and only where there is a form to submit its inputs with. Without one the checkbox
		// would post nothing, so the server would see no confirm_shown, fall back to its no-JS
		// "resubmit unchanged to confirm" branch and let the address through whether the user ticked
		// the box or not. Rendering nothing leaves that fallback honest.
		if (!fallbackForm) {
			return chosen;
		}

		return chosen.concat(
			[...withoutForm.values()]
				.filter((anchor) => !claimed.has(anchor.id))
				.map((anchor) => ({ anchor, form: fallbackForm }))
		);
	}

	/**
	 * Render the confirmation checkbox after any soft validation failure on the page.
	 *
	 * The server emits an empty anchor span inside the validation message, and only in soft mode,
	 * so there is nothing to render unless confirming is actually allowed.
	 */
	renderConfirmations() {
		this.getConfirmationAnchors().forEach(({ anchor, form }, index) => {
			if (anchor.dataset.datatoolsConfirmInit) {
				return;
			}
			anchor.dataset.datatoolsConfirmInit = "1";

			const key = anchor.id.replace("datatools-address-confirm-", "");

			if (!key) {
				return;
			}

			// The wrapper stays beside the anchor, which is the copy the user is reading, so where
			// that sits outside the form the inputs are tied to it by the HTML form attribute
			// instead. That puts them in form.elements, which is what both a native submission and
			// jQuery's serialize() read, so the confirmation posts from either place.
			const detached = !form.contains(anchor);

			if (detached && !form.id) {
				form.id = `datatools-address-form-${index}`;
			}

			// The key is only unique within one form, so two address-enabled forms on a page can
			// both produce a field_3 or a group_1. Without qualifying the id, label[for] resolves
			// to whichever came first in the DOM and clicking one form's label ticks the other's.
			const scope = form.id ? form.id : `anchor${index}`;

			const wrapper = document.createElement("span");
			wrapper.className = "datatools-address-confirm";

			const checkbox = document.createElement("input");
			checkbox.type = "checkbox";
			checkbox.value = "1";
			checkbox.name = `datatools_address_confirmed[${key}]`;
			checkbox.id = `datatools_address_confirmed_${scope}_${key}`;

			const label = document.createElement("label");
			label.setAttribute("for", checkbox.id);
			label.textContent = Settings.getAddressConfirmationLabel();

			// Tells the server the checkbox was offered, so an unticked resubmission is not
			// mistaken for the no-JS "submit again to confirm" fallback.
			const shown = document.createElement("input");
			shown.type = "hidden";
			shown.name = `datatools_address_confirm_shown[${key}]`;
			shown.value = "1";

			if (detached) {
				checkbox.setAttribute("form", form.id);
				shown.setAttribute("form", form.id);
			}

			wrapper.appendChild(checkbox);
			wrapper.appendChild(label);
			wrapper.appendChild(shown);

			anchor.parentNode.insertBefore(wrapper, anchor.nextSibling);
		});
	}
}

const AddressValidationInstance = new AddressValidation();
export default AddressValidationInstance;
